import clsx from 'clsx';
import { forwardRef, type MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type StylingProps,
  type AriaLabelingProps,
  type DOMProps,
} from '@dynatrace/strato-components/core';
import { XmarkIcon } from '@dynatrace/strato-icons';

import * as styles from './ChipDeleteButton.sty.js';
import { useChipContext } from './contexts/useChipContext.js';

//#region Typings
/**
 * Accepted properties for the Chip Button.
 * @public
 */
export interface ChipDeleteButtonProps
  extends AriaLabelingProps,
    DOMProps,
    StylingProps,
    DataTestId {
  /**
   * If a button is disabled e.g. it cannot be interacted with.
   * By default, it inherits the disabled prop of the Chip, but can be overwritten.
   * @defaultValue false
   */
  disabled?: boolean;
  /** Called when the button is interacted with. */
  onClick?: MouseEventHandler;
}
//#endregion

/**
 * Component which allows users to trigger actions or events when on click or when pressing `Enter` or `Space` while the button has focus.
 * @public
 */
export const ChipDeleteButton = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  ChipDeleteButtonProps
>((props, forwardedRef) => {
  const intl = useIntl();
  const deleteChip = intl.formatMessage({
    defaultMessage: 'Delete chip',
    id: 'JfsC0aOiAl7G3esV',
    description: 'Aria label used to describe the delete chip button.',
  });

  const {
    disabled: propDisabled,
    'aria-label': ariaLabelProp = deleteChip,
    'aria-describedby': ariaDescribedByProp,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: ChipDeleteButtonProps = props;

  const {
    disabled,
    variant,
    color,
    size = 'default',
    valueId,
  } = useChipContext();

  return (
    <Button
      ref={forwardedRef}
      data-testid={dataTestId}
      variant={variant}
      color={color}
      disabled={propDisabled ?? disabled}
      aria-label={ariaLabelProp}
      aria-describedby={ariaDescribedByProp || valueId}
      className={clsx(styles.base, styles.size[size], consumerClassName)}
      style={consumerStyle}
      {...remainingProps}
    >
      <Button.Prefix>
        <XmarkIcon size={size === 'default' ? size : 'small'} />
      </Button.Prefix>
    </Button>
  );
});

(
  ChipDeleteButton as typeof ChipDeleteButton & { displayName: string }
).displayName = 'Chip.DeleteButton';
